import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  CircularProgress, Alert, Breadcrumbs, Link as MuiLink
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import RoleAssignmentDialog from './RoleAssignmentDialog';
import RevokeConfirmDialog from './RevokeConfirmDialog';

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const loadUser = useCallback(async () => {
    setLoading(true);
    try {
      // The listUsers endpoint returns all users; find this one
      const data = await authService.listUsers(200, 0);
      const allUsers = data?.users ?? data ?? [];
      const found = allUsers.find(u => String(u.user_id) === String(id));
      if (found) {
        setUser(found);
        setError(null);
      } else {
        setError('User not found');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/admin/users" underline="hover" color="inherit">
          Users
        </MuiLink>
        <Typography color="text.primary">{user?.display_name || user?.email}</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {user?.display_name || 'Unknown User'}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Firebase UID: {user?.firebase_uid || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {user?.created_at ? new Date(user.created_at).toLocaleString() : '-'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Role Assignments</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAssignDialogOpen(true)}
        >
          Assign Role
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Organization Scope</TableCell>
              <TableCell>Facility Scope</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {user?.orgAssignments?.length > 0 ? (
              user.orgAssignments.map((assignment) => (
                <TableRow key={assignment.assignment_id}>
                  <TableCell>
                    <Chip
                      label={assignment.role?.role_name || 'unknown'}
                      size="small"
                      color={assignment.role?.role_name === 'admin' ? 'error' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>{assignment.org_entity_id || 'All'}</TableCell>
                  <TableCell>
                    {assignment.facility_entity_ids?.length > 0
                      ? assignment.facility_entity_ids.join(', ')
                      : 'All'}
                  </TableCell>
                  <TableCell>
                    {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setRevokeTarget(assignment)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No role assignments. Click "Assign Role" to add one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <RoleAssignmentDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        userId={id}
        onSuccess={loadUser}
      />

      <RevokeConfirmDialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        assignment={revokeTarget}
        onSuccess={loadUser}
      />
    </Box>
  );
}
